import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

// ── Employee "Time Off" page (PTO Phase 2) ────────────────────────────────────
// Visible to any internal staff member with a pto_profile. Shows the balance
// breakdown + expiration, a request form with a live computed total, and a
// My Requests list with edit/cancel while allowed. Not Admin/Manager-gated.

const REASONS = ['Vacation', 'Personal Time', 'Medical', 'Family Related', 'Other'];
const CANCELLABLE = new Set(['Pending', 'Approved']);

function days(halfDays) {
  if (halfDays == null) return '—';
  const d = halfDays / 2;
  return `${d} day${d === 1 ? '' : 's'}`;
}
function statusBadge(status) {
  const map = {
    Pending: 'adm-badge adm-badge-writer',
    Approved: 'adm-badge adm-badge-shoot',
    Taken: 'adm-badge adm-badge-post',
    Denied: 'adm-badge adm-badge-admin',
    Cancelled: 'adm-badge',
  };
  return map[status] || 'adm-badge';
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function isFutureStart(startDate) {
  return startDate > todayStr();
}

function emptyForm() {
  return { startDate: '', endDate: '', halfStart: false, halfEnd: false, reason: 'Vacation', supervisorName: '', note: '' };
}

export default function TimeOff() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null); // { totalHalfDays, pastDated }
  const [pastConfirm, setPastConfirm] = useState(false);
  const [warn, setWarn] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get('/api/pto/me');
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load your Time Off page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live total whenever the range/toggles change.
  useEffect(() => {
    if (!form.startDate || !form.endDate) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/api/pto/me/preview', {
          startDate: form.startDate, endDate: form.endDate, halfStart: form.halfStart, halfEnd: form.halfEnd,
        });
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [form.startDate, form.endDate, form.halfStart, form.halfEnd]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const resetForm = () => { setForm(emptyForm()); setEditingId(null); setPreview(null); setPastConfirm(false); setWarn(''); };

  const submit = async () => {
    setWarn('');
    if (!form.startDate || !form.endDate) { setWarn('Choose a start and end date.'); return; }
    const past = isPastStart(form.startDate);
    if (past && !pastConfirm) { setWarn('This date is in the past — please confirm you already took the PTO.'); return; }
    setSubmitting(true);
    try {
      const body = { ...form, pastConfirmed: past ? pastConfirm : false };
      if (editingId) await api.put(`/api/pto/me/requests/${editingId}`, body);
      else await api.post('/api/pto/me/requests', body);
      resetForm();
      await load();
    } catch (e) {
      setWarn(e.response?.data?.error || 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      startDate: r.startDate, endDate: r.endDate, halfStart: r.halfStart, halfEnd: r.halfEnd,
      reason: r.reason, supervisorName: r.supervisorName || '', note: r.note || '',
    });
    setPastConfirm(!!r.pastConfirmed);
    setWarn('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancel = async (r) => {
    if (!window.confirm('Cancel this time-off request?')) return;
    try { await api.delete(`/api/pto/me/requests/${r.id}`); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to cancel.'); }
  };

  // A request whose start date has passed is locked — file a change request
  // instead of editing.
  const requestChange = async (r) => {
    const description = window.prompt('What change do you need to this past PTO entry?');
    if (description == null) return;
    if (!description.trim()) { setErr('A description is required.'); return; }
    const reason = window.prompt('Why? (reason)');
    if (reason == null) return;
    if (!reason.trim()) { setErr('A reason is required.'); return; }
    try {
      await api.post(`/api/pto/me/requests/${r.id}/change-request`, { description: description.trim(), reason: reason.trim() });
      setErr('');
      window.alert('Change request submitted — an approver will review it.');
      await load();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to submit change request.'); }
  };

  if (loading) return <div className="main"><div className="body" style={{ padding: 24 }}>Loading…</div></div>;

  if (data && data.enrolled === false) {
    return (
      <div className="main">
        <div className="topbar"><div><div className="page-title">Time Off</div></div></div>
        <div className="body" style={{ padding: 24, color: 'var(--tx-muted)' }}>
          You're not enrolled in PTO yet. An admin needs to set your hire date first.
        </div>
      </div>
    );
  }

  const b = data?.balance || {};
  const requests = data?.requests || [];
  const pastSelected = form.startDate && isPastStart(form.startDate);
  const canEditThis = (r) => r.status === 'Pending' || (r.status === 'Approved' && isFutureStart(r.startDate));

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Time Off</div>
          <div className="page-sub">Request time off and track your PTO balance</div>
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 24 }}>
        {err && <div style={{ color: 'var(--s-incomplete-t)' }}>{err}</div>}

        {/* Balance breakdown */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>
            Your Balance
            {b.benefitYearEnd && (
              <span style={{ fontWeight: 400, color: 'var(--tx-faint)', fontSize: 12, marginLeft: 8 }}>
                (benefit year ends {typeof b.benefitYearEnd === 'string' ? b.benefitYearEnd.slice(0, 10) : new Date(b.benefitYearEnd).toISOString().slice(0, 10)} — unused days expire)
              </span>
            )}
          </div>
          {b.status === 'First90' ? (
            /* New employee: the grant lands at the 90-day mark, so show the
               PROJECTED entitlement + eligibility date rather than a 0 balance. */
            <>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <Stat label="Available to request" value={days(b.availableToRequest)} strong />
                <Stat label="Projected entitlement" value={days(b.projectedEntitlement)} />
                <Stat label="Pending" value={days(b.pending)} />
                <Stat label="Approved (upcoming)" value={days(b.approvedUpcoming)} />
              </div>
              <div style={{ color: 'var(--tx-muted)', fontSize: 12, marginTop: 8 }}>
                As a new employee, {days(b.projectedEntitlement)} become available on{' '}
                <strong>{b.firstGrantDate || 'your eligibility date'}</strong> (your 90-day mark).
                PTO can't start before then.
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <Stat label="Available" value={days(b.available)} strong />
                <Stat label="Granted" value={days(b.granted)} />
                <Stat label="Taken" value={days(b.taken)} />
                <Stat label="Approved (upcoming)" value={days(b.approvedUpcoming)} />
                <Stat label="Pending" value={days(b.pending)} />
                {b.adjustments ? <Stat label="Adjustments" value={days(b.adjustments)} /> : null}
                {b.forfeited ? <Stat label="Forfeited" value={days(b.forfeited)} /> : null}
              </div>
              {b.available < 0 && (
                <div style={{ color: 'var(--s-incomplete-t)', fontSize: 12, marginTop: 8 }}>
                  Your balance is negative — this usually means past-dated PTO exceeded your grant.
                </div>
              )}
            </>
          )}
        </div>

        {/* Request form */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>{editingId ? 'Edit Request' : 'Request Time Off'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: 12 }}>
            <label className="vc-field"><span>Start date</span>
              <input className="vc-input" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
              <label style={{ fontSize: 12, marginTop: 4 }}>
                <input type="checkbox" checked={form.halfStart} onChange={e => set('halfStart', e.target.checked)} /> Half day (start)
              </label>
            </label>
            <label className="vc-field"><span>End date</span>
              <input className="vc-input" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
              <label style={{ fontSize: 12, marginTop: 4 }}>
                <input type="checkbox" checked={form.halfEnd} onChange={e => set('halfEnd', e.target.checked)} /> Half day (end)
              </label>
            </label>
            <label className="vc-field"><span>Reason</span>
              <select className="vc-input" value={form.reason} onChange={e => set('reason', e.target.value)}>
                {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="vc-field"><span>Supervisor (optional)</span>
              <input className="vc-input" value={form.supervisorName} onChange={e => set('supervisorName', e.target.value)} placeholder="Who approved this?" />
            </label>
            <label className="vc-field" style={{ gridColumn: '1 / -1' }}><span>Note (optional)</span>
              <input className="vc-input" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Anything your manager should know" />
            </label>
          </div>

          <div style={{ marginTop: 12, fontSize: 13 }}>
            {preview
              ? <>Computed total: <strong>{days(preview.totalHalfDays)}</strong>{preview.totalHalfDays === 0 && ' (no working days in range)'}</>
              : <span style={{ color: 'var(--tx-faint)' }}>Pick a date range to see the total.</span>}
          </div>

          {pastSelected && (
            <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--s-incomplete-t)', borderRadius: 6 }}>
              <div style={{ fontSize: 13, marginBottom: 6 }}>This date is in the past — please confirm you already took the PTO.</div>
              <label style={{ fontSize: 13 }}>
                <input type="checkbox" checked={pastConfirm} onChange={e => setPastConfirm(e.target.checked)} /> Yes, I already took this time off
              </label>
            </div>
          )}

          {warn && <div style={{ color: 'var(--s-incomplete-t)', fontSize: 13, marginTop: 10 }}>{warn}</div>}

          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={submit} disabled={submitting || !preview || preview.totalHalfDays === 0}>
              {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Submit request'}
            </button>
            {editingId && <button className="btn-ghost" onClick={resetForm}>Cancel edit</button>}
          </div>
        </div>

        {/* My Requests */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>My Requests</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th">Dates</th>
                  <th className="adm-th">Days</th>
                  <th className="adm-th">Reason</th>
                  <th className="adm-th">Status</th>
                  <th className="adm-th"></th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && (
                  <tr><td className="adm-td" colSpan={5} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: 24 }}>No requests yet.</td></tr>
                )}
                {requests.map(r => (
                  <tr key={r.id}>
                    <td className="adm-td">{r.startDate} → {r.endDate}{(r.halfStart || r.halfEnd) ? ' ½' : ''}</td>
                    <td className="adm-td">{days(r.totalHalfDays)}</td>
                    <td className="adm-td">{r.reason}</td>
                    <td className="adm-td">
                      <span className={statusBadge(r.status)}>{r.status}</span>
                      {r.status === 'Denied' && r.denialReason && (
                        <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{r.denialReason}</div>
                      )}
                    </td>
                    <td className="adm-td">
                      {CANCELLABLE.has(r.status) && canEditThis(r) && (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => startEdit(r)}>Edit</button>
                          <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => cancel(r)}>Cancel</button>
                        </span>
                      )}
                      {(r.status === 'Taken' || (r.status === 'Approved' && !isFutureStart(r.startDate))) && (
                        <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => requestChange(r)}>Request a change</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function isPastStart(startDate) {
  return startDate < todayStr();
}

function Stat({ label, value, strong }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--tx-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: strong ? 20 : 16, fontWeight: strong ? 700 : 500 }}>{value}</div>
    </div>
  );
}
